import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

import { getDeviceType } from '../../../../lib/utils';

import CrossIconPath from '../../assets/stroked-cross.svg?react';
import SpecialCharTooltip, { specialChars } from '../../../special-char-tooltip';
import { offset } from 'caret-pos';
import { useOnClickOutside } from '../../../../lib/hooks';

export const Container = styled.div`
    display: inline-flex;
    position: relative;

    max-width: 100%;
    min-height: 32px;
    margin: calc(var(--space-unit) / 2) 0;
`;

export const CrossIconButton = styled.button`
    background: transparent;
    border: none;
    outline: none;

    cursor: pointer;

    height: 30px;
    width: 30px;

    position: absolute;
    top: 3px;
    right: 0;
`;

export const CrossIcon = styled(CrossIconPath)`
    ${({ theme }) => css`
        fill: ${theme.colors['surface-grey-700']};
    `}
`;

export const IconContainer = styled.div`
    display: none;

    ${Container}:focus-within & {
        ${(props) =>
            !props.disabled &&
            !props.isEmpty &&
            css`
                display: block;
            `}
    }
`;

export const HiddenCanvas = styled.canvas`
    display: none;
    width: 0;
    height: 0;
`;

export const Answer = styled.textarea`
    resize: none;
    box-sizing: border-box;
    border: var(--border-size-small) solid var(--gray200);

    padding: calc(var(--space-unit) / 2);
    font-size: 16px;
    font-family: 'IBM Plex Sans';
    min-width: 88px;
    min-height: 32px;
    overflow: hidden;

    ${(props) =>
        props.isEmpty &&
        props.disabled &&
        css`
            cursor: initial;
            pointer-events: none;
            box-shadow: none;
        `};
    ${(props) =>
        !props.isEmpty &&
        props.disabled &&
        css`
            border: var(--border-size-small) solid var(--gray800);
            color: var(--gray800);
            cursor: initial;
            pointer-events: none;
        `};

    ${(props) =>
        props.isEmpty &&
        !props.disabled &&
        css`
            box-shadow: inset var(--shadow-2);
        `};

    ${(props) =>
        !props.isEmpty &&
        !props.disabled &&
        css`
            border: var(--border-size-small) solid var(--blue400);
            background: var(--blue50);
            color: var(--gray800);
        `};

    &:hover {
        ${(props) =>
            props.isEmpty &&
            !props.disabled &&
            css`
                border: var(--border-size-small) solid var(--gray800);
                background: var(--gray50);
                cursor: pointer;
            `}
        ${(props) =>
            !props.isEmpty &&
            !props.disabled &&
            css`
                border: var(--border-size-small) solid var(--blue700);
                color: var(--blue700);
                box-shadow: inset var(--shadow-2);
                cursor: pointer;
            `}
    }

    ${Container}:focus-within & {
        ${(props) =>
            props.isEmpty &&
            !props.disabled &&
            css`
                cursor: text;
                border: var(--border-size-small) solid var(--blue400);
                box-shadow: var(--outline);
                outline: none;
            `}
        ${(props) =>
            !props.isEmpty &&
            !props.disabled &&
            css`
                cursor: text;
                color: var(--blue400);
                border: var(--border-size-small) solid var(--blue400);
                background: var(--color-white);
                padding-right: calc(var(--space-unit) * 4);
                box-shadow: var(--outline);
                outline: none;
            `}
    }

    &:active {
        ${(props) =>
            props.isEmpty &&
            !props.disabled &&
            css`
                cursor: text;
                box-shadow: none;
            `}
        ${(props) =>
            !props.isEmpty &&
            !props.disabled &&
            css`
                cursor: text;
                box-shadow: none;
                background: var(--blue50);
                color: var(--gray800);
            `}
    }
`;

const StyledTextarea = ({ id, disabled = false, onChange, value = '' }) => {
    const canvasRef = useRef();
    const textAreaRef = useRef();
    const componentRef = useRef();
    const isDesktop = getDeviceType() === 'DESKTOP';

    const [text, setText] = useState(value);
    const [width, setWidth] = useState(88);
    const [canvasContext, setCanvasContext] = useState();

    const [show, setShow] = useState(false);
    const [caretPosition, setCaretPosition] = useState({ left: 10, top: 10 });
    const [char, setChar] = useState('');
    const [up, setUp] = useState('');
    const [prevChar, setPrevChar] = useState('');

    const handleKeyDown = (e) => {
        setShow(false);
        const insertedChar = e.key;
        const currentChar = insertedChar.toLowerCase() in specialChars ? insertedChar : null;
        if (!!currentChar && currentChar === prevChar && currentChar !== up) {
            // Long press
            e.preventDefault();
            setShow(true);
            setChar(insertedChar);
            const currentCaretPosition = offset(textAreaRef.current);
            setCaretPosition(currentCaretPosition);
        } else if (
            !isNaN(insertedChar) &&
            specialChars[char.toLowerCase()] &&
            parseInt(insertedChar) <= specialChars[char.toLowerCase()].length
        ) {
            // Number key pressed when tooltip open
            e.preventDefault();
            const uppercase = char === char.toUpperCase();
            const key = specialChars[char.toLowerCase()][parseInt(insertedChar) - 1];
            setChar('');
            handleClose(uppercase ? key.toUpperCase() : key);
        } else {
            setChar('');
            setUp('');
        }
        setPrevChar(currentChar);
    };

    useEffect(() => {
        const context = canvasRef.current.getContext('2d');

        context.font = "16px 'IBM Plex Sans'";
        setCanvasContext(context);
    }, []);

    useEffect(() => {
        if (canvasContext) {
            const { width: canvasWidth } = canvasContext.measureText(text);

            if (canvasWidth + 40 > 88) {
                setWidth(canvasWidth + 40);
            }

            if (canvasWidth + 40 < 88) {
                setWidth(88);
            }
        }
    }, [text, canvasContext]);

    useEffect(() => {
        textAreaRef.current.style.height = 'auto';
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }, [width, textAreaRef]);

    useOnClickOutside(componentRef, () => {
        setShow(false);
    });

    const handleClose = (key) => {
        const { value: textAreaValue, selectionStart, selectionEnd } = textAreaRef.current;
        setText(textAreaValue.substring(0, selectionStart - 1) + key + textAreaValue.substring(selectionEnd, textAreaValue.length));
        setShow(false);
        textAreaRef.current.focus();
    };

    return (
        <div ref={componentRef} style={{ display: 'inline-block' }}>
            <Container tabIndex={!disabled ? '-1' : undefined} className={`textarea${id}`}>
                <HiddenCanvas ref={canvasRef} />

                <Answer
                    autoCapitalize="none"
                    id={`${id}_textarea`}
                    rows={1}
                    ref={textAreaRef}
                    style={{ width }}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={() => onChange(text)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            textAreaRef.current.blur();
                        } else {
                            handleKeyDown(e);
                        }
                    }}
                    onKeyUp={(e) => setUp(e.key)}
                    disabled={disabled}
                    isEmpty={!text || !/\S/.test(text)}
                    value={text || ' '} /* The space is used to avoid textarea wrong position */
                />

                <IconContainer
                    disabled={disabled}
                    isEmpty={!text}
                    onClick={() => {
                        setText('');
                        onChange('');
                    }}
                >
                    <CrossIconButton type="button" aria-label="Cancella tutto il testo">
                        <CrossIcon aria-hidden="true" />
                    </CrossIconButton>
                </IconContainer>
            </Container>
            {isDesktop && show && (
                <SpecialCharTooltip handleClose={handleClose} target={textAreaRef.current} char={char} position={caretPosition} />
            )}
        </div>
    );
};

StyledTextarea.propTypes = {
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
};

export default StyledTextarea;
